import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateShipmentStandaloneFormGroup } from './create-shipment-standalone-form-group.interface';
import { CreateShipmentStandaloneLineFormGroup } from './create-shipment-standalone-line-form-group.interface';

export interface CreateShipmentStandaloneLineDefaults {
  inventoryItemId: string | null;
  warehouseId: string | null;
  quantity: number;
  unitOfMeasureId: number | null;
  salesOrderLineId: string | null;
}

/**
 * Reactive-form aggregate for the standalone create-shipment modal.
 * Provided per modal instance because the shape is tied to the modal's lifecycle.
 */
@Injectable()
export class CreateShipmentStandaloneFormModel {
  public readonly fieldNames = {
    customerId: 'customerId',
    salesOrderId: 'salesOrderId',
    shipmentDate: 'shipmentDate',
    lines: 'lines',
    line: {
      inventoryItemId: 'inventoryItemId',
      warehouseId: 'warehouseId',
      quantity: 'quantity',
      unitOfMeasureId: 'unitOfMeasureId',
      salesOrderLineId: 'salesOrderLineId',
    },
  } as const;

  private readonly formBuilder = inject(FormBuilder);

  public readonly form: FormGroup<CreateShipmentStandaloneFormGroup> =
    this.formBuilder.group<CreateShipmentStandaloneFormGroup>({
      customerId: new FormControl<string | null>(null, { validators: [Validators.required] }),
      salesOrderId: new FormControl<string | null>(null),
      shipmentDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
      lines: this.formBuilder.array<FormGroup<CreateShipmentStandaloneLineFormGroup>>([]),
    });

  public get lines(): FormArray<FormGroup<CreateShipmentStandaloneLineFormGroup>> {
    return this.form.controls.lines;
  }

  public addLine(
    defaults: CreateShipmentStandaloneLineDefaults,
  ): FormGroup<CreateShipmentStandaloneLineFormGroup> {
    const group = CreateShipmentStandaloneFormModel.buildLineFormGroup(this.formBuilder, defaults);
    this.lines.push(group);
    return group;
  }

  public clearLines(): void {
    while (this.lines.length > 0) {
      this.lines.removeAt(0);
    }
  }

  public removeLineAt(index: number): void {
    if (index >= 0 && index < this.lines.length) {
      this.lines.removeAt(index);
    }
  }

  public reset(): void {
    this.clearLines();
    this.form.reset({
      customerId: null,
      salesOrderId: null,
      shipmentDate: null,
    });
  }

  public static buildLineFormGroup(
    formBuilder: FormBuilder,
    defaults: CreateShipmentStandaloneLineDefaults,
  ): FormGroup<CreateShipmentStandaloneLineFormGroup> {
    return formBuilder.group<CreateShipmentStandaloneLineFormGroup>({
      inventoryItemId: new FormControl<string | null>(defaults.inventoryItemId, {
        validators: [Validators.required],
      }),
      warehouseId: new FormControl<string | null>(defaults.warehouseId, {
        validators: [Validators.required],
      }),
      quantity: new FormControl<number | null>(defaults.quantity, {
        validators: [Validators.required, Validators.min(0.0001)],
      }),
      unitOfMeasureId: new FormControl<number | null>(defaults.unitOfMeasureId, {
        validators: [Validators.required],
      }),
      salesOrderLineId: new FormControl<string | null>(defaults.salesOrderLineId),
      // Default `false` — Product/Hybrid is the safe default. The flag is flipped exclusively
      // by `applyServiceWarehouseRule` once the inventory-item id resolves to a Service item.
      isServiceLine: new FormControl<boolean>(false, { nonNullable: true }),
    });
  }

  /**
   * Toggles the `warehouseId` validators AND the `isServiceLine` flag on a standalone-shipment
   * line based on whether the currently selected inventory item is a Service.
   *
   * Service items are shipped without warehouse decrement on the backend, so the FE must
   * drop the required-validator and null the value when the operator picks a Service item.
   * Product/Hybrid lines keep the `Validators.required` rule.
   *
   * This method is the ONLY writer for `isServiceLine` — consumers must never set the
   * control directly. Both the validator state and the derived flag are mutated together
   * to keep the form state and the rendered view in lock-step (no parallel array).
   */
  public static applyServiceWarehouseRule(
    line: FormGroup<CreateShipmentStandaloneLineFormGroup>,
    isServiceItem: boolean,
  ): void {
    const warehouseControl = line.controls.warehouseId;
    if (isServiceItem) {
      warehouseControl.clearValidators();
      warehouseControl.setValue(null, { emitEvent: false });
    } else {
      warehouseControl.setValidators([Validators.required]);
    }
    warehouseControl.updateValueAndValidity({ emitEvent: false });
    line.controls.isServiceLine.setValue(isServiceItem, { emitEvent: false });
  }
}
